import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface DashboardData {
  conversations?: { active: number; total: number }
  outreach?: { daily_sent: number; daily_limit: number }
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--c-surface-faint)' }}>
      <span style={{ fontSize: 13, color: 'var(--c-ink-muted)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-ink)' }}>{value}</span>
    </div>
  )
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--c-surface-border)',
      borderRadius: 12, padding: '18px 22px', flex: 1,
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-ink)' }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-ink-soft)', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--c-ink-faint)', marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

export default function WhatsApp() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState<string | null>(null)

  const fetchDashboard = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await apiClient.get<DashboardData>('/dashboard')
      setDashboard(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos del canal')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchDashboard() }, [fetchDashboard])

  if (loading) return <LoadingSpinner label="Cargando información del canal…" />

  const dailySent   = dashboard?.outreach?.daily_sent ?? 0
  const dailyLimit  = dashboard?.outreach?.daily_limit ?? 50
  const pctUsed     = dailyLimit > 0 ? Math.round((dailySent / dailyLimit) * 100) : 0
  const convActive  = dashboard?.conversations?.active ?? 0
  const convTotal   = dashboard?.conversations?.total ?? 0

  return (
    <div>

      {error && <ErrorAlert message={error} />}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Conversaciones activas" value={convActive} sub={`${convTotal} en total`} />
        <StatCard label="Outreach enviado hoy" value={dailySent} sub={`límite: ${dailyLimit}`} />
        <StatCard label="Cuota usada" value={`${pctUsed}%`} sub={pctUsed >= 80 ? '⚠ Cerca del límite' : 'dentro del límite'} />
      </div>

      {/* Número registrado */}
      <div style={{
        background: '#fff', border: '1px solid var(--c-surface-border)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 16,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)', marginBottom: 14 }}>
          Número WhatsApp Business
        </div>
        <InfoRow label="Número WABA" value="+51 924 266 042" />
        <InfoRow label="Entorno" value="TEST (coe-automation)" />
        <InfoRow label="Estado de registro" value={
          <span style={{ background: 'var(--c-brand-light)', color: 'var(--c-brand-ink)', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
            CONECTADO
          </span>
        } />
        <InfoRow label="Proveedor" value="AWS End User Messaging Social" />
        <InfoRow label="Tipo de cuenta" value="WhatsApp Business API (WABA)" />
        <InfoRow label="E2E validado" value="Sí — mensaje entrante + respuesta Gemma confirmados" />
      </div>

      {/* Barra de cuota */}
      <div style={{
        background: '#fff', border: '1px solid var(--c-surface-border)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 16,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)', marginBottom: 14 }}>
          Cuota de contacto proactivo — hoy
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--c-ink-muted)', marginBottom: 8 }}>
          <span>{dailySent} enviados</span>
          <span>límite {dailyLimit}</span>
        </div>
        <div style={{ height: 10, borderRadius: 99, background: 'var(--c-surface-border)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 99, transition: 'width 0.4s ease',
            width: `${Math.min(pctUsed, 100)}%`,
            background: pctUsed >= 80 ? 'var(--c-danger)' : 'var(--c-brand)',
          }} />
        </div>
        {pctUsed >= 80 && (
          <div style={{ fontSize: 12, color: 'var(--c-danger)', marginTop: 8, fontWeight: 600 }}>
            Al 80% del límite — nuevos contactos proactivos serán bloqueados.
          </div>
        )}
      </div>

      {/* Flujo técnico */}
      <div style={{
        background: '#fff', border: '1px solid var(--c-surface-border)',
        borderRadius: 12, padding: '20px 24px',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)', marginBottom: 14 }}>
          Flujo de ingesta (E2E validado)
        </div>
        {[
          'Meta WABA → AWS End User Messaging Social',
          'SNS topic: pgh-telemarketing-test-waba-events',
          'SQS inbound: pgh-telemarketing-test-inbound',
          'Lambda fn-conversation → Bedrock Gemma 3 (12B)',
          'socialmessaging.send_whatsapp_message() → respuesta al usuario',
        ].map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', background: 'var(--c-brand-light)',
              color: 'var(--c-brand-hover)', fontWeight: 800, fontSize: 11,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {i + 1}
            </div>
            <div style={{ fontSize: 13, color: 'var(--c-ink-soft)', paddingTop: 3, fontFamily: "'IBM Plex Mono', monospace" }}>
              {step}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
